import { useQuery } from '@tanstack/react-query';

import { isISO2 } from '@/constants/countries/ISO2';

import { queryFn } from './queryFn';

type Args = {
  market: string;
  industry: number;
  startUnix: number;
  endUnix: number;
  points?: number;
  enabled?: boolean;
  refetchOnMount?: boolean;
  staleTime?: number;
};

/**
 * ### Description
 *
 * This hook returns an IndustryTimeseries as per https://studio.apollographql.com/graph/Simply-Wall-St-5peegj/variant/prod/schema/reference?query=IndustryTimeseries
 *
 * ### Arguments
 * ```
 * type Args = {
 *   market: PrimaryCountryISO;
 *   industry: number;
 *   startUnix: number;
 *   endUnix: number;
 *   points?: number;
 *   // react-query
 *   enabled?: boolean;
 *   refetchOnMount?: boolean;
 *   staleTime?: number;
 * };
 * ```
 *
 */
export const useIndustryTimeseries = ({
  market,
  industry,
  startUnix,
  endUnix,
  points,
  enabled = true,
  refetchOnMount = false,
  staleTime = Infinity,
}: Args) => {
  const upperCaseMarket = market?.toUpperCase();
  const ISO2 = isISO2(upperCaseMarket) ? upperCaseMarket : 'US';
  return useQuery({
    queryKey: [
      'IndustryTimeseries',
      ISO2,
      industry,
      startUnix,
      endUnix,
      points,
    ],
    queryFn,
    enabled,
    staleTime,
    refetchOnMount,
  });
};
